import { type Avatar, avatarDrawing, type SvgNode } from "@talkers/shared";
import { createMemo, For, type JSX, Match, Switch } from "solid-js";

/**
 * 猫アバター。図形の定義は `@talkers/shared` の `avatarDrawing` が単一の出所で、
 * CLI や静的書き出し (`avatarSvg`) と同じものを描く。
 *
 * タグごとに要素を書き分けているのは、SVG 名前空間を Solid のコンパイラに
 * 静的に解決させるため (動的タグでは HTML 要素として作られてしまう)。
 */
function Leaf(props: { node: SvgNode }): JSX.Element {
  const attrs = () => props.node.attrs as Record<string, never>;
  return (
    <Switch>
      <Match when={props.node.tag === "circle"}>
        <circle {...attrs()} />
      </Match>
      <Match when={props.node.tag === "ellipse"}>
        <ellipse {...attrs()} />
      </Match>
      <Match when={props.node.tag === "path"}>
        <path {...attrs()} />
      </Match>
      <Match when={props.node.tag === "rect"}>
        <rect {...attrs()} />
      </Match>
      <Match when={props.node.tag === "line"}>
        <line {...attrs()} />
      </Match>
    </Switch>
  );
}

function Node(props: { node: SvgNode }): JSX.Element {
  const attrs = () => props.node.attrs as Record<string, never>;
  const children = () => (
    <For each={props.node.children ?? []}>{(child) => <Node node={child} />}</For>
  );
  return (
    <Switch fallback={<Leaf node={props.node} />}>
      <Match when={props.node.tag === "g"}>
        <g {...attrs()}>{children()}</g>
      </Match>
      <Match when={props.node.tag === "defs"}>
        <defs {...attrs()}>{children()}</defs>
      </Match>
      <Match when={props.node.tag === "clipPath"}>
        <clipPath {...attrs()}>{children()}</clipPath>
      </Match>
    </Switch>
  );
}

export function CatAvatar(props: {
  avatar: Avatar;
  size?: number;
  label?: string;
  class?: string;
}): JSX.Element {
  const drawing = createMemo(() => avatarDrawing(props.avatar));
  const size = () => props.size ?? 36;

  return (
    <svg
      width={size()}
      height={size()}
      viewBox={drawing().viewBox}
      role="img"
      aria-label={props.label ?? "猫アバター"}
      class={`cat-avatar${props.class === undefined ? "" : ` ${props.class}`}`}
    >
      <For each={drawing().nodes}>{(node) => <Node node={node} />}</For>
    </svg>
  );
}
